import { Injectable, inject } from '@angular/core'
import { Client } from '@stomp/stompjs'
import { TechniqueNames } from '../remote-services/dtos/technique.dto'
import { UUID } from '../models/utils.type'
import { KeycloakService } from '../../keycloak/keycloak.service'
import { State } from '../../core/store'
import { Store } from '@ngrx/store'
import { AsyncTasksActions } from '../../core/store/async-tasks/async-task.actions'
import {
  WebSocketSubscription,
  WebSocketTopics,
  WsEvents,
} from './webSocket.type'
import { WsEventsActions } from '../../core/store/ws-events/ws-events.actions'

@Injectable({
  providedIn: 'root',
})
export class WebsocketService {
  private readonly keycloak = inject(KeycloakService)
  private readonly store = inject<Store<State>>(Store)

  private stompClient: Client | null

  private readonly sub: Map<WebSocketTopics, WebSocketSubscription> = new Map()

  async connect(): Promise<void> {
    if (this.stompClient) return
    return new Promise(resolve => {
      this.stompClient = new Client({
        webSocketFactory: () =>
          new WebSocket(`/ws?access_token=${this.keycloak.getToken()}`),
      })

      this.stompClient.onConnect = () => {
        this.sub.forEach((v, k) => {
          switch (k) {
            case 'events':
              this.subscribeEvents(v.technique, v.jobsiteId)
              break
            case 'async-tasks':
              this.subscribeAsyncTasks(v.technique, v.jobsiteId)
              break
          }
        })
        resolve()
      }

      this.stompClient.activate()
    })
  }

  async subscribeAsyncTasks(
    technique: TechniqueNames,
    jobsiteId: UUID,
  ): Promise<void> {
    await this.connect()
    if (this.stompClient.connected) {
      this.sub.get('async-tasks')?.sub.unsubscribe()
      this.sub.set('async-tasks', {
        jobsiteId,
        technique,
        sub: this.stompClient.subscribe(
          `/topic/async-tasks/${technique}/${jobsiteId}`,
          msg => {
            this.store.dispatch(
              AsyncTasksActions.setAsyncTasks.createSetItems(
                JSON.parse(msg.body),
              ),
            )
          },
        ),
      })
    } else {
      setTimeout(() => this.subscribeAsyncTasks(technique, jobsiteId), 200)
    }
  }

  async subscribeEvents(
    technique: TechniqueNames,
    jobsiteId: UUID,
  ): Promise<void> {
    await this.connect()
    if (this.stompClient.connected) {
      this.sub.get('events')?.sub.unsubscribe()
      this.sub.set('events', {
        jobsiteId,
        technique,
        sub: this.stompClient.subscribe(
          `/topic/events/${technique}/${jobsiteId}`,
          msg => {
            this.store.dispatch(
              WsEventsActions.handleEvent.create(msg.body as WsEvents),
            )
          },
        ),
      })
    } else {
      setTimeout(() => this.subscribeEvents(technique, jobsiteId), 200)
    }
  }

  unsubscribeEvents(): void {
    if (Boolean(this.sub) && this.sub.has('events')) {
      this.sub.get('events').sub.unsubscribe()
      this.sub.delete('events')
    }
  }
  unsubscribeAsyncTasks(): void {
    if (Boolean(this.sub) && this.sub.has('async-tasks')) {
      this.sub.get('async-tasks')?.sub.unsubscribe()
      this.sub.delete('async-tasks')
    }
    this.store.dispatch(AsyncTasksActions.setAsyncTasks.createSetItems([]))
  }

  close(): void {
    if (this.stompClient.connected) this.stompClient.deactivate()
  }
}
